<script lang="ts">
  /**
   * Grafer over sensorene i én kasse: velg periode og hvilke serier som vises.
   * Hver serie får sin egen lille graf (felles tidsakse), så prosent og °C ikke
   * blandes på én akse. Hold fingeren på en graf for å se verdien. Valgene huskes
   * på denne enheten. Logikken ligger i lib/grafer.ts.
   */
  import { onMount } from 'svelte';
  import type { Potte, PottePlanteFull } from '../lib/database.types';
  import { hentSensorHistorikk, type SensorHistorikkRad } from '../lib/sensorhistorikk';
  import { sensorOppsett } from '../lib/sensorer';
  import { sensorEtikett, kasseForm } from '../lib/utils';
  import {
    PERIODER, tilgjengeligeSerier, seriePunkter, grafKalibrering, forenkle, delVedHull, hullGrense, akse, naermeste,
    lesGrafvalg, lagreGrafvalg, type PeriodeId, type SerieId, type Punkt,
  } from '../lib/grafer';

  let {
    potte,
    pottePlanter,
    forvalgt = null,
    naa = Date.now(),
  }: {
    potte: Potte;
    pottePlanter: PottePlanteFull[];
    /** Serien som skal vises når arket åpnes fra et felt eller tanken. */
    forvalgt?: SerieId | null;
    naa?: number;
  } = $props();

  const oppsett = $derived(sensorOppsett(potte));
  const skille = $derived((potte.skillevegger ?? [false, false]) as boolean[]);
  const serier = $derived(
    tilgjengeligeSerier(oppsett, (plass) => {
      const pp = pottePlanter.find((p) => p.seksjon === plass);
      return pp ? `${pp.plante.navn} (${sensorEtikett(plass, skille, kasseForm(potte)).toLowerCase()})` : sensorEtikett(plass, skille, kasseForm(potte));
    }),
  );

  const lagret = lesGrafvalg();
  let periode = $state<PeriodeId>(lagret.periode ?? '7d');
  let valgt = $state<SerieId[]>([]);
  let rader = $state<SensorHistorikkRad[]>([]);
  let laster = $state(true);
  let feil = $state<string | null>(null);

  onMount(() => {
    const finnes = new Set(serier.map((s) => s.id));
    const start = (lagret.serier ?? serier.filter((s) => s.id !== 'rh').map((s) => s.id)).filter((id) => finnes.has(id));
    if (forvalgt && finnes.has(forvalgt)) valgt = [forvalgt];
    else valgt = start.length ? start : serier.map((s) => s.id);
  });

  const dager = $derived(PERIODER.find((p) => p.id === periode)?.dager ?? 7);
  const fra = $derived(naa - dager * 86_400_000);

  $effect(() => {
    const id = potte.potte_id;
    const d = dager;
    laster = true;
    feil = null;
    hentSensorHistorikk(id, d, naa)
      .then((r) => (rader = r))
      .catch(() => (feil = 'Fikk ikke hentet historikken. Sjekk nettet og prøv igjen.'))
      .finally(() => (laster = false));
  });

  function velgPeriode(p: PeriodeId) {
    periode = p;
    lagreGrafvalg(valgt, periode);
  }

  function veksle(id: SerieId) {
    valgt = valgt.includes(id) ? valgt.filter((x) => x !== id) : [...valgt, id];
    lagreGrafvalg(valgt, periode);
  }

  // Tegneflaten (viewBox-enheter). Venstre marg til aksetallene.
  const B = 320;
  const H = 96;
  const ML = 26;
  const MT = 6;
  const MB = 16;

  // Kassas kalibrering: tom/full per tank og egen tørr/våt for jordsensorene.
  const kal = $derived(grafKalibrering(potte));
  const grafer = $derived(
    serier
      .filter((s) => valgt.includes(s.id))
      .map((def) => {
        const raa = seriePunkter(rader, def.id, kal);
        const punkter = forenkle(raa, fra, naa);
        const a = akse(def, punkter);
        const x = (t: number) => ML + ((t - fra) / (naa - fra)) * (B - ML - 2);
        const y = (v: number) => MT + (1 - (v - a.min) / (a.max - a.min || 1)) * (H - MT - MB);
        const stier = delVedHull(punkter, hullGrense(fra, naa, raa.length)).map((bit) =>
          bit.length === 1
            ? `M${x(bit[0].t) - 1},${y(bit[0].v)}h2`
            : bit.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(''),
        );
        return { def, punkter, a, x, y, stier, siste: raa[raa.length - 1] ?? null };
      }),
  );

  function tidTekst(t: number, kort = false): string {
    const d = new Date(t);
    if (dager <= 1) return d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    const dato = d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'numeric' });
    return kort ? dato : `${dato} ${d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`;
  }

  const tall = (v: number, enhet: string) => `${enhet === '°C' ? v.toFixed(1) : Math.round(v)} ${enhet}`;

  // Fingeren over en graf: tidspunkt i viewBox-koordinater.
  let peker = $state<{ id: SerieId; t: number } | null>(null);
  function flytt(e: PointerEvent, id: SerieId) {
    const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
    const vx = ((e.clientX - rect.left) / rect.width) * B;
    const t = fra + ((vx - ML) / (B - ML - 2)) * (naa - fra);
    peker = { id, t: Math.min(naa, Math.max(fra, t)) };
  }
  const pekerPunkt = (id: SerieId, punkter: Punkt[]) => (peker?.id === id ? naermeste(punkter, peker.t) : null);
</script>

<div class="font-display text-[25px] font-semibold leading-[1.05]">Grafer</div>
<div class="font-mono text-[11px] text-text-muted mt-1">{potte.navn ?? potte.potte_id}</div>

<div class="flex gap-1.5 mt-4" role="group" aria-label="Periode">
  {#each PERIODER as p (p.id)}
    <button
      class="text-xs px-3 py-1.5 rounded-lg border transition-colors {periode === p.id
        ? 'bg-leaf/10 text-leaf-glow border-leaf/30'
        : 'bg-surface-raised text-text-muted border-border'}"
      onclick={() => velgPeriode(p.id)}
      aria-pressed={periode === p.id}>{p.navn}</button
    >
  {/each}
</div>

{#if serier.length === 0}
  <p class="text-sm text-text-muted mt-4">Kassa har ingen sensorer valgt. Velg dem under tannhjulet på kassa.</p>
{:else}
  <div class="flex flex-wrap gap-1.5 mt-3" role="group" aria-label="Hva som vises">
    {#each serier as s (s.id)}
      <button
        class="text-xs px-2.5 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5 {valgt.includes(s.id)
          ? 'bg-surface-hover text-text border-border-strong'
          : 'bg-surface-raised text-text-muted border-border'}"
        onclick={() => veksle(s.id)}
        aria-pressed={valgt.includes(s.id)}
      >
        <span class="w-2 h-2 rounded-full" style="background:{s.farge}; opacity:{valgt.includes(s.id) ? 1 : 0.35}"></span>
        {s.navn}
      </button>
    {/each}
  </div>

  {#if feil}
    <p class="text-sm text-rose mt-4">{feil}</p>
  {:else if laster && rader.length === 0}
    <p class="text-sm text-text-muted mt-4">Henter historikken …</p>
  {:else if valgt.length === 0}
    <p class="text-sm text-text-muted mt-4">Velg hva du vil se over.</p>
  {:else}
    <div class="flex flex-col gap-4 mt-4">
      {#each grafer as g (g.def.id)}
        {@const pp = pekerPunkt(g.def.id, g.punkter)}
        <section>
          <div class="flex items-baseline justify-between gap-2">
            <h3 class="text-sm">{g.def.navn}</h3>
            <span class="font-mono text-[12px] text-text-muted">
              {#if pp}{tidTekst(pp.t)} · <span class="text-text">{tall(pp.v, g.def.enhet)}</span>
              {:else if g.siste}nå <span class="text-text">{tall(g.siste.v, g.def.enhet)}</span>{:else}ingen data{/if}
            </span>
          </div>
          <svg
            viewBox="0 0 {B} {H}"
            class="w-full h-auto mt-1 touch-none select-none"
            role="img"
            aria-label="{g.def.navn}, {PERIODER.find((p) => p.id === periode)?.navn}"
            onpointerdown={(e) => flytt(e, g.def.id)}
            onpointermove={(e) => (e.buttons || e.pointerType === 'mouse') && flytt(e, g.def.id)}
            onpointerleave={() => (peker = null)}
            onpointerup={(e) => e.pointerType !== 'mouse' && (peker = null)}
          >
            {#each [g.a.max, (g.a.max + g.a.min) / 2, g.a.min] as v, i (i)}
              <line x1={ML} x2={B - 2} y1={g.y(v)} y2={g.y(v)} stroke="currentColor" class="text-border" stroke-width="0.5" />
              <text x={ML - 3} y={g.y(v) + 3} text-anchor="end" class="fill-text-dim" font-size="8">{Math.round(v)}</text>
            {/each}
            {#each g.stier as d, i (i)}
              <path {d} fill="none" stroke={g.def.farge} stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" />
            {/each}
            {#if pp}
              <line x1={g.x(pp.t)} x2={g.x(pp.t)} y1={MT} y2={H - MB} stroke="currentColor" class="text-text-muted" stroke-width="0.6" />
              <circle cx={g.x(pp.t)} cy={g.y(pp.v)} r="2.6" fill={g.def.farge} />
            {/if}
            <text x={ML} y={H - 3} class="fill-text-dim" font-size="8">{tidTekst(fra, true)}</text>
            <text x={(ML + B) / 2} y={H - 3} text-anchor="middle" class="fill-text-dim" font-size="8">{tidTekst((fra + naa) / 2, true)}</text>
            <text x={B - 2} y={H - 3} text-anchor="end" class="fill-text-dim" font-size="8">nå</text>
          </svg>
          {#if g.punkter.length === 0}
            <p class="text-[11px] text-text-dim">Ingen målinger i perioden.</p>
          {/if}
        </section>
      {/each}
    </div>
    <p class="text-[11px] text-text-dim mt-3">Hull i linja betyr at kassa ikke sendte data. Hold fingeren på en graf for å se verdien.</p>
  {/if}
{/if}
